import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { Dashboard, Tile } from "../../../../api/types/dashboardsTypes";
import type { CancellationToken, ReportInfo, ReportValidationResponse } from "../../../../api/types/svTypes";
import { isSmartViewWidget } from "../models/isTileObjectSmartView";

export type SelectedCompany = {
  code: string;
  dashboards: SelectedCompanyDashboard[];
};

export type SelectedCompanyDashboard = {
  companyCode: string;
  dashboard: Dashboard;
  tiles: SelectedCompanyTile[];
};

export type SelectedCompanyTile = {
  tile: Tile;
  companyCode: string;
  dashboard: Dashboard;
  report?: ReportInfo;
  validating: boolean;
  validated: boolean;
  result?: ReportValidationResponse;
  error?: boolean;
  ct?: CancellationToken;
};

export type ValidationState = {
  companies: SelectedCompany[];
  reportsInfo: ReportInfo[];
  reportsInfoLoaded: boolean;
};

const initialState: ValidationState = { companies: [], reportsInfo: [], reportsInfoLoaded: false };

const validationStateSlice = createSlice({
  name: "validationState",
  initialState: initialState,
  reducers: {
    setReportsInfoAction: (state, action: PayloadAction<ReportInfo[]>) => {
      state.reportsInfo = action.payload;
      state.reportsInfoLoaded = true;
      state.companies.forEach((c) =>
        c.dashboards.forEach((d) =>
          d.tiles.forEach((t) => {
            t.report = state.reportsInfo.find(
              (r) => isSmartViewWidget(t.tile.widget) && r.reportId === t.tile.widget.reportId
            );
          })
        )
      );
    },
    setCompaniesAction: (state, action: PayloadAction<{ companyCodes: string[]; dashboards: Dashboard[] }>) => {
      const newCompanyList = action.payload.companyCodes.map((code): SelectedCompany => {
        const alreadySelectedCompany = state.companies.find((c) => c.code === code);
        if (alreadySelectedCompany) {
          return alreadySelectedCompany;
        }

        return {
          code,
          dashboards: action.payload.dashboards.map((d) => ({
            companyCode: code,
            dashboard: d,
            validating: false,
            validated: false,
            tiles: d.tiles.map((t) => ({
              tile: t,
              companyCode: code,
              dashboard: d,
              report: state.reportsInfo.find((r) => isSmartViewWidget(t.widget) && r.reportId === t.widget.reportId),
              validating: false,
              validated: false,
              result: undefined,
              error: undefined,
              ct: undefined,
            })),
          })),
        };
      });
      state.companies = newCompanyList;
    },
    removeCompanyAction: (state, action: PayloadAction<string>) => {
      const company = state.companies.find((c) => c.code === action.payload);
      if (company) {
        company.dashboards.forEach((r) => r.tiles.forEach((r) => r.ct?.cancel()));
        const newCompanyList = state.companies.filter((c) => c.code !== action.payload);
        state.companies = newCompanyList;
      }
    },
    removeDashboardAction: (state, action: PayloadAction<{ companyDashboard: SelectedCompanyDashboard }>) => {
      const company = state.companies.find((c) => c.code === action.payload.companyDashboard.companyCode);
      if (company) {
        const dashboard = company.dashboards.find(
          (r) => r.dashboard.dashboardId === action.payload.companyDashboard.dashboard.dashboardId
        );
        if (dashboard) {
          dashboard.tiles.forEach((r) => r.ct?.cancel());
          company.dashboards = company.dashboards.filter(
            (r) => r.dashboard.dashboardId !== action.payload.companyDashboard.dashboard.dashboardId
          );
        }
        if (company.dashboards.length === 0) {
          company.dashboards.forEach((r) => r.tiles.forEach((r) => r.ct?.cancel()));
          const newCompanyList = state.companies.filter((c) => c.code !== action.payload.companyDashboard.companyCode);
          state.companies = newCompanyList;
        }
      }
    },
    removeTileAction: (state, action: PayloadAction<{ tile: SelectedCompanyTile }>) => {
      const company = state.companies.find((c) => c.code === action.payload.tile.companyCode);
      if (company) {
        const dashboard = company.dashboards.find(
          (r) => r.dashboard.dashboardId === action.payload.tile.dashboard.dashboardId
        );
        if (dashboard) {
          dashboard.tiles = dashboard.tiles.filter((r) => r.tile.tileId !== action.payload.tile.tile.tileId);
        }
      }
    },
    updateTileAction: (
      state,
      action: PayloadAction<{
        dashboard: Dashboard;
        tile: SelectedCompanyTile;
        changes: Partial<SelectedCompanyTile>;
      }>
    ) => {
      const company = state.companies.find((c) => c.code === action.payload.tile.companyCode);
      if (company) {
        const dashboard = company.dashboards.find(
          (r) => r.dashboard.dashboardId === action.payload.tile.dashboard.dashboardId
        );
        if (dashboard) {
          const tileIndex = dashboard.tiles.findIndex((r) => r.tile.tileId === action.payload.tile.tile.tileId);
          if (tileIndex !== -1) {
            const currentTile = dashboard.tiles[tileIndex];
            dashboard.tiles[tileIndex] = {
              ...currentTile,
              ...action.payload.changes,
            } as SelectedCompanyTile;
          }
        }
      }
    },
  },
});

export default validationStateSlice;

export const actions = validationStateSlice.actions;
